import { useEffect, useCallback } from 'react';
import { signOut } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';

const INACTIVITY_TIMEOUT = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; // Show warning 5 minutes before timeout

export const useInactivityTimeout = () => {
  const navigate = useNavigate();

  const resetTimer = useCallback(() => {
    // Clear existing timeout
    if (window.inactivityTimeout) {
      clearTimeout(window.inactivityTimeout);
    }
    if (window.warningTimeout) {
      clearTimeout(window.warningTimeout);
    }

    // Set warning timeout
    window.warningTimeout = setTimeout(() => {
      // Show warning notification
      const timeLeft = Math.round(WARNING_BEFORE_TIMEOUT / 1000 / 60);
      const warningEvent = new CustomEvent('inactivityWarning', {
        detail: { timeLeft }
      });
      window.dispatchEvent(warningEvent);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT);

    // Set logout timeout
    window.inactivityTimeout = setTimeout(async () => {
      try {
        // Clear all timeouts
        if (window.inactivityTimeout) {
          clearTimeout(window.inactivityTimeout);
        }
        if (window.warningTimeout) {
          clearTimeout(window.warningTimeout);
        }

        // Sign out and redirect to login
        await signOut();
        navigate('/login', { 
          replace: true,
          state: { message: 'You have been logged out due to inactivity.' }
        });
      } catch (error) {
        console.error('Error during auto-logout:', error);
      }
    }, INACTIVITY_TIMEOUT);
  }, [navigate]);

  useEffect(() => {
    // Events to track user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    // Reset timer on user activity
    const handleUserActivity = () => {
      resetTimer();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity);
    });

    // Initial timer setup
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
      if (window.inactivityTimeout) {
        clearTimeout(window.inactivityTimeout);
      }
      if (window.warningTimeout) {
        clearTimeout(window.warningTimeout);
      }
    };
  }, [resetTimer]);
}; 